import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

// Components
import Sidebar from './SiderBar';
import QuestionPage from './QuestionPage';
import ResultCard from './ResultCard';
import HeadingCard from '../../../common/element/Card/HeadingCard';
import Button from '../../../common/element/Buttons';
import Footer from '../Footer';

// ✅ Import the new ExpiredTokenPage component
// (Adjust the path '../../common/components/ExpiredTokenPage' if you saved it elsewhere)
import ExpiredTokenPage from '../ExpiredTokenPage';
import LoginFormFORprospect from '../LoginFormFORprospect';
import { submitSessionUrl } from '../../../redux/SessionUrl/sessionUrlSlice';
import SweetAlertService from '../../../common/SweetAlert/SweetAlert';
import { selectSubmitSessionUrlData } from '../../../redux/SessionUrl/sessionUrlSelectors';

// Redux Actions
import { fetchNcodes, submitQuiz, checkTokenExpiry } from '../../../redux/tool/toolSlice';
import ThreeDotBouncingLoader from '../../../common/element/ButtonLoader/ThreeDotBouncingLoder';
import InvalidTokenPage from '../InvalidToken';

const InnerDrive = () => {
  const [currentView, setCurrentView] = useState('instructions');
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Clear prospect token from localStorage on mount (handles refresh)
  useEffect(() => {
    try {
      localStorage.removeItem('prospect_token');
    } catch (e) { }
  }, []);

  // ✅ Get token from URL query parameters
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // ✅ Get prospect company params from URL
  const companyIdParam = searchParams.get('company_id');
  const companyNameParam = searchParams.get('company_name');
  const companySessionIdParam = searchParams.get('company_session_id');
  const sessionNameParam = searchParams.get('session_name');
  const sessionTypeParam = searchParams.get('session_type');
  const isSessionUrlFlow = sessionTypeParam === 'session_url';
  const isInterviewCandidate = searchParams.get('is_interview_candidate') === 'true';
  const isProspect = searchParams.get('is_prospect') === 'true';

  // Whether prospect login should be shown before main flow
  const storedProspectToken = (() => { try { return localStorage.getItem('prospect_token'); } catch (e) { return null; } })();
  const [prospectRequired] = useState(Boolean(companyIdParam && companyNameParam));
  const [prospectConfirmed, setProspectConfirmed] = useState(false);
  const [prospectToken, setProspectToken] = useState(storedProspectToken || token || null);
  const [sessionUrlUserData, setSessionUrlUserData] = useState(null);

  // Redux Selectors - Questions & Submission
  const questionsData = useSelector(state => state.tool.ncodes.data);
  const isLoading = useSelector(state => state.tool.ncodes.loading);
  const error = useSelector(state => state.tool.ncodes.error);
  const quizSubmitData = useSelector(state => state.tool.quizSubmit.data);
  const quizSubmitLoading = useSelector(state => state.tool.quizSubmit.loading);
  const submitSessionUrlData = useSelector(selectSubmitSessionUrlData);

  // ✅ Redux Selectors - Token Expiry
  const tokenExpiryData = useSelector(state => state.tool.tokenExpiryCheck.data);
  const tokenExpiryLoading = useSelector(state => state.tool.tokenExpiryCheck.loading);
  console.log(tokenExpiryData, "tokenExpiryDatatokenExpiryData")
  // 1. Check Token Expiry on Mount
  useEffect(() => {
    if (token) {
      dispatch(checkTokenExpiry(token));
    }
  }, [dispatch, token]);

  // 2. Fetch Questions
  useEffect(() => {
    if (!prospectRequired || prospectConfirmed) {
      dispatch(fetchNcodes("inner_drive"));
    }
  }, [dispatch, prospectRequired, prospectConfirmed]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMobileMenuOpen]);

  // Transform flat question list into pairs (2 questions per step)
  const getTransformedQuestions = () => {
    if (!questionsData || !Array.isArray(questionsData)) return [];

    const groupedQuestions = [];
    for (let i = 0; i < questionsData.length; i += 2) {
      const pair = questionsData.slice(i, i + 2).map(question => ({
        id: question.id,
        question: question.question,
        options: question?.answers?.map(answer => ({
          id: `q${question.id}_p${answer.id}`,
          text: answer.answer
        }))
      }));
      groupedQuestions.push(pair);
    }
    return groupedQuestions;
  };

  const transformedQuestions = getTransformedQuestions();
  const totalSteps = transformedQuestions.length;

  // Start Test Handler
  const handleStartTest = () => {
    if (transformedQuestions.length > 0) {
      setCurrentView('test');
      setActiveStep(1);
    }
  };

  // Sidebar Step Click Handler
  const handleStepClick = (stepId) => {
    if (activeStep === 0) return;

    const maxReachableStep = Math.max(...completedSteps, activeStep);
    if (stepId <= maxReachableStep || stepId === 1) {
      setActiveStep(stepId);
      setCurrentView('test');
      setIsMobileMenuOpen(false); // Close menu when step is clicked
    }
  };

  // Update answers state
  const handleAnswerChange = (stepAnswers) => {
    setAnswers(prev => ({
      ...prev,
      [activeStep]: stepAnswers
    }));
  };

  // Extract ID number from string (e.g., "q1_p2" -> 2)
  const extractAnswerIdFromOption = (optionId) => {
    const parts = optionId.split('_p');
    return parseInt(parts[1]);
  };

  // Prepare data for API
  const convertAnswersToAPIFormat = () => {
    const apiFormat = {};

    Object.keys(answers).forEach(stepNumber => {
      const stepAnswers = answers[stepNumber];
      const stepQuestions = transformedQuestions[parseInt(stepNumber) - 1];
      if (!stepQuestions) return;

      if (stepAnswers.question1?.priority1 && stepAnswers.question1?.priority2) {
        const q1Id = stepQuestions[0].id;
        const p1Value = extractAnswerIdFromOption(stepAnswers.question1.priority1);
        const p2Value = extractAnswerIdFromOption(stepAnswers.question1.priority2);
        apiFormat[q1Id] = [p1Value, p2Value];
      }

      if (stepAnswers.question2?.priority1 && stepAnswers.question2?.priority2) {
        const q2Id = stepQuestions[1].id;
        const p1Value = extractAnswerIdFromOption(stepAnswers.question2.priority1);
        const p2Value = extractAnswerIdFromOption(stepAnswers.question2.priority2);
        apiFormat[q2Id] = [p1Value, p2Value];
      }
    });

    return apiFormat;
  };

  // Handle Next / Submit
  const handleNext = async () => {
    if (!completedSteps.includes(activeStep)) {
      setCompletedSteps(prev => [...prev, activeStep]);
    }

    if (activeStep < totalSteps) {
      setActiveStep(activeStep + 1);
    } else {
      // Final Submit
      if (!completedSteps.includes(activeStep)) {
        setCompletedSteps(prev => [...prev, activeStep]);
      }

      const finalAnswers = convertAnswersToAPIFormat();

      if (isSessionUrlFlow) {
        const resultAction = await dispatch(
          submitSessionUrl({
            company_session_id: Number(companySessionIdParam),
            name: sessionUrlUserData?.name || "",
            email: sessionUrlUserData?.email || "",
            phone: sessionUrlUserData?.phone || "",
            result_json: finalAnswers,
          })
        );
        if (submitSessionUrl.rejected.match(resultAction)) {
          SweetAlertService.showError({
            title: "Error!",
            message: resultAction.payload || "Something went wrong. Please try again.",
          });
          return;
        }
        if (submitSessionUrl.fulfilled.match(resultAction)) {
          SweetAlertService.showSuccess({
            title: "Success!",
            message: "Test has been submitted successfully.",
            onConfirm: () => {
              const allSteps = Array.from({ length: totalSteps }, (_, i) => i + 1);
              setCompletedSteps(allSteps);
              setActiveStep(0);
            },
          });
        }
        return;
      }

      // Submit with payload and token
      const effectiveToken = prospectToken || token || null;
      dispatch(submitQuiz({ payload: finalAnswers, token: effectiveToken }))
        .unwrap()
        .then((response) => {
          const allSteps = Array.from({ length: totalSteps }, (_, i) => i + 1);
          setCompletedSteps(allSteps);
          setActiveStep(0);
        })
        .catch((err) => {
          console.error('Submission failed:', err);
          alert('ERROR: ' + (err.response?.data?.message || err.message || err));
        });
    }
  };

  const handlePrevious = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  // ============================================
  // CONDITIONAL RENDER LOGIC
  // ============================================

  // 1. Check if Token is being verified
  if (tokenExpiryLoading) {
    return (
      <div className="min-h-screen bg-[#FFF1E6] flex items-center justify-center">
        <div className="text-center">
          <ThreeDotBouncingLoader />
        </div>
      </div>

    );
  }
  // ✅ 2 If tokenExpiryData exists and is not empty, the token is invalid/used.
  if (tokenExpiryData?.token_valid == false && tokenExpiryData?.expired == false) {
    return <InvalidTokenPage />;
  }
  if (tokenExpiryData?.expired == true) {
    return <ExpiredTokenPage />;
  }

  // 3. Prospect Login View
  if (prospectRequired && !prospectConfirmed) {
    const headerOverrides = {
      title: 'Discover Your Top 2 Inner Drives Assessment',
      subtitle: 'Enter your details to access the tool',
      highlighted: ''
    };

    const initialValues = {
      company_id: Number(companyIdParam) || 0,
      company_name: companyNameParam || ''
    };

    return (
      <LoginFormFORprospect
        headerOverrides={headerOverrides}
        initialValues={initialValues}
        sessionType={sessionTypeParam}
        sessionName={sessionNameParam}
        companySessionId={companySessionIdParam}
        showProspectFields={isProspect}
        onSuccess={(payload) => {
          if (isSessionUrlFlow && payload?.userData) {
            setSessionUrlUserData(payload.userData);
          }
          setProspectConfirmed(true);
          const returnedToken = payload?.token || payload?.data?.token || null;
          if (returnedToken) setProspectToken(returnedToken);
          dispatch(fetchNcodes("inner_drive"));
        }}
      />
    );
  }

  // 4. Loading Questions
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF8E1C] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  // 5. Error Loading Questions
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>Error loading questions: {error}</p>
          </div>
          <Button
            onClick={() => dispatch(fetchNcodes("inner_drive"))}
            className="bg-[#FF8E1C] text-white px-4 py-2 rounded"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // 6. No Questions Available
  if (!transformedQuestions || transformedQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No questions available.</p>
          <Button
            onClick={() => dispatch(fetchNcodes("inner_drive"))}
            className="bg-[#FF8E1C] text-white px-4 py-2 rounded"
          >
            Reload
          </Button>
        </div>
      </div>
    );
  }

  // 7. Main UI
  return (
    <div className="min-h-screen bg-[#FFF1E6] flex">
      {/* Desktop Sidebar */}
      <div className="hidden md-993:block">
        <Sidebar
          activeStep={activeStep}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
          totalSteps={totalSteps}
        />
      </div>

      {/* Mobile Sidebar - Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-sidebar-overlay md-993:hidden" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="mobile-sidebar-container" onClick={(e) => e.stopPropagation()}>
            <Sidebar
              activeStep={activeStep}
              completedSteps={completedSteps}
              onStepClick={handleStepClick}
              totalSteps={totalSteps}
            />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col">
        <div className="bg-[#FFF1E6] flex-1">
          <div className="p-2 md:p-6 max-w-8xl mx-auto">
            {/* Mobile Header */}
            <div className='mobile-responsive'>
              <div className="">
                <img src="./../assets/images/Logo-Incredible-Leaders.png" width={120} alt="Incredible Leaders" />
              </div>
              <div
                className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                role="button"
                aria-label="Toggle menu"
                tabIndex={0}
              >
                <svg width="24" height="19" viewBox="0 0 32 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 1.52381C0 0.682225 0.68224 0 1.52381 0H30.4762C31.3178 0 32 0.682225 32 1.52381C32 2.36539 31.3178 3.04762 30.4762 3.04762H1.52381C0.68224 3.04762 0 2.36539 0 1.52381Z" fill="black" />
                  <path d="M0 12.1902C0 11.3486 0.68224 10.6662 1.52381 10.6662L18.2857 10.6667C19.1273 10.6667 19.8095 11.3489 19.8095 12.1905C19.8095 13.0321 19.1273 13.7143 18.2857 13.7143L1.52381 13.714C0.68224 13.714 0 13.0316 0 12.1902Z" fill="black" />
                  <path d="M1.52381 21.333C0.68224 21.333 0 22.0152 0 22.8568C0 23.6983 0.68224 24.3805 1.52381 24.3805H27.4286C28.2702 24.3805 28.9524 23.6983 28.9524 22.8568C28.9524 22.0152 28.2702 21.333 27.4286 21.333H1.52381Z" fill="black" />
                </svg>
              </div>
            </div>

            {/* Content Area */}
            {(quizSubmitData || (isSessionUrlFlow && submitSessionUrlData)) ? (
              <ResultCard result={isSessionUrlFlow ? submitSessionUrlData : quizSubmitData} token={prospectToken || token} isInterviewCandidate={isInterviewCandidate || isProspect}  />
            ) : currentView === 'instructions' ? (
              <div>
                <HeadingCard title="Important Instructions">
                  <ul className="space-y-4">
                    {[
                      "In this assessment, you will see 10 questions. Each question has 6 options.",
                      "You have to choose 2 options as answer and mark them priority wise.",
                      "For the option you relate to the most - Mark it as Priority 1.",
                      "For the option you relate to next - Mark it as Priority 2.",
                      "Your answers should depict the real you."
                    ].map((text, i) => (
                      <li key={i} className="flex items-start md:items-center lg:items-center gap-4">
                        <span className="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 bg-[#FF8E1C] rounded-full font-lato not-italic font-semibold text-[13px] md:text-[14px] leading-[17px] text-white flex items-center justify-center mt-0.5">
                          {i + 1}
                        </span>
                        <p className="font-lato not-italic font-bold text-[16px] md:text-[20px] lg:text-[20px] mobile_fix leading-[115%] text-black text-start md:text-center">{text}</p>
                      </li>
                    ))}
                  </ul>
                </HeadingCard>
                <Button
                  onClick={handleStartTest}
                  className="mt-[32px] w-[150px] h-[48px] rounded-[12px] text-white font-lato font-semibold text-[16px] leading-[115%] bg-[#FF8E1C] border border-[#FFF4E5]/50 shadow-[0px_2px_5px_#FFF4E5]"
                >
                  Start This Test
                </Button>
              </div>
            ) : (
              transformedQuestions[activeStep - 1] && (
                <QuestionPage
                  questions={transformedQuestions[activeStep - 1]}
                  answers={answers[activeStep] || {}}
                  allAnswers={answers}
                  onAnswerChange={handleAnswerChange}
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  isFirst={activeStep === 1}
                  isLast={activeStep === totalSteps}
                  currentStep={activeStep}
                  totalSteps={totalSteps}
                  Button={Button}
                  isSubmitting={quizSubmitLoading}
                />
              )
            )}
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default InnerDrive;